import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Typography, Button } from "@mui/material";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { ChevronDownIcon } from "lucide-react";
import {
  BodypartType,
  ExerciseTagDto,
  MovementPatternType,
} from "@growth-machine-llc/stridist-api-client";
import { ExerciseFiltersContent } from "../exercise-library/filters/ExerciseFiltersContent";
import {
  BodypartTypeLabels,
  MovementPatternTypeLabels,
} from "../exercise-library/constants";
import makeStyles from "@mui/styles/makeStyles";
import {
  getFilterStatus,
  getTagStatus,
  mapFilters,
} from "../exercise-library/filters/filterUtils";
import {
  NONE_TAG_ID,
  TagsFiltersContent,
} from "../exercise-library/filters/TagsFiltersContent";
import {
  allBodyparts,
  allMovementPatterns,
} from "../exercise-library/ExercisesTableScreen";
import { BadgeCounter } from "../counter/BadgeCounter";

const useStyles = makeStyles((theme) => ({
  button: {
    borderRadius: theme.spacing(1),
    textTransform: "none",
    fontWeight: 600,
    width: "100%",
  },
}));

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  border: "none",
  boxShadow: "none",
  "&.MuiPaper-root": {
    boxShadow: "none",
  },
  "&:not(:last-child)": { borderBottom: 0 },
  "&::before": { display: "none" },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ChevronDownIcon size={18} />} {...props} />
))(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  boxShadow: "none",
  [`& .${accordionSummaryClasses.expanded}`]: {
    boxShadow: "none",
  },
  [`&.${accordionSummaryClasses.root}`]: {
    padding: theme.spacing(1, 2),
    boxShadow: "none",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`& .${accordionSummaryClasses.content}`]: { margin: 0, padding: 0 },
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    color: theme.palette.primary.main,
    transition: "transform 0.2s ease",
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    { transform: "rotate(180deg)" },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(1),
  maxHeight: 250,
  boxShadow: "none",
  borderBottom: `1px solid ${theme.palette.divider}`,
  overflowY: "auto",
}));

function resetFiltersFromArray<T extends string | number>(
  allValues: T[],
): Record<T, boolean> {
  return allValues.reduce(
    (acc, key) => {
      acc[key] = true;
      return acc;
    },
    {} as Record<T, boolean>,
  );
}

type AccordionSection = "movement" | "bodypart" | "tags";

type ExerciseAccordionFilters = {
  movementPatterns?: MovementPatternType[];
  bodyparts?: BodypartType[];
  exerciseTags?: ExerciseTagDto[];
  selectedTagIds: number[];
  onMovementChange?: (values: MovementPatternType[]) => void;
  onBodypartChange?: (values: BodypartType[]) => void;
  onTagChange?: (values: number[]) => void;
  onApply?: () => void;
  onActiveCountChange?: (count: number) => void;
};

export function ExerciseFiltersAccordion({
  movementPatterns,
  bodyparts,
  exerciseTags,
  selectedTagIds,
  onMovementChange,
  onBodypartChange,
  onTagChange,
  onApply,
  onActiveCountChange,
}: ExerciseAccordionFilters) {
  const s = useStyles();

  const [tempMovementFilters, setTempMovementFilters] = React.useState(() =>
    mapFilters(MovementPatternType, movementPatterns),
  );
  const [tempBodypartFilters, setTempBodypartFilters] = React.useState(() =>
    mapFilters(BodypartType, bodyparts),
  );
  const [tempTagFilters, setTempTagFilters] =
    React.useState<number[]>(selectedTagIds);

  const movementActiveStatus = React.useMemo(
    () => getFilterStatus(tempMovementFilters),
    [tempMovementFilters],
  );
  const bodypartActiveStatus = React.useMemo(
    () => getFilterStatus(tempBodypartFilters),
    [tempBodypartFilters],
  );
  const tagActiveStatus = React.useMemo(
    () => getTagStatus(exerciseTags, tempTagFilters),
    [tempTagFilters, exerciseTags],
  );

  const resetAll = () => {
    const resetMovement = resetFiltersFromArray(allMovementPatterns);
    const resetBodypart = resetFiltersFromArray(allBodyparts);

    const resetTags = exerciseTags
      ? [...exerciseTags.map((tag) => tag.id), NONE_TAG_ID]
      : [];
    setTempMovementFilters(resetMovement);
    setTempBodypartFilters(resetBodypart);
    setTempTagFilters(resetTags);
  };

  const applyFilters = () => {
    onMovementChange?.(
      Object.entries(tempMovementFilters)
        .filter(([_, checked]) => checked)
        .map(([key]) => key as MovementPatternType),
    );
    onBodypartChange?.(
      Object.entries(tempBodypartFilters)
        .filter(([_, checked]) => checked)
        .map(([key]) => key as BodypartType),
    );
    onTagChange?.(tempTagFilters);

    const totalActive =
      movementActiveStatus?.activeCount +
      bodypartActiveStatus?.activeCount +
      tagActiveStatus?.activeCount;

    onActiveCountChange?.(totalActive);

    onApply?.();
  };

  const sections = [
    {
      id: "movement",
      title: "Movement patterns",
      status: movementActiveStatus,
      content: (
        <ExerciseFiltersContent
          filters={tempMovementFilters}
          filterTypesLabels={MovementPatternTypeLabels}
          onChange={setTempMovementFilters}
        />
      ),
    },
    {
      id: "bodypart",
      title: "Bodyparts",
      status: bodypartActiveStatus,
      content: (
        <ExerciseFiltersContent
          filters={tempBodypartFilters}
          filterTypesLabels={BodypartTypeLabels}
          onChange={setTempBodypartFilters}
        />
      ),
    },
    exerciseTags?.length > 0 && {
      id: "tags",
      title: "Tags",
      activeCount: tagActiveStatus,
      content: (
        <TagsFiltersContent
          selectedTagIds={tempTagFilters}
          onTagChange={setTempTagFilters}
          tags={exerciseTags}
        />
      ),
    },
  ].filter(Boolean);

  const defaultExpanded =
    (sections.find((s) => s.status?.activeCount > 0)?.id as AccordionSection) ||
    (sections[0]?.id as AccordionSection) ||
    false;
  const [expanded, setExpanded] = React.useState<AccordionSection | false>(
    defaultExpanded,
  );

  return (
    <div>
      {sections.map(({ id, title, status, content }) => (
        <Accordion
          key={id}
          expanded={expanded === id}
          onChange={(_, v) => setExpanded(v ? (id as AccordionSection) : false)}
        >
          <AccordionSummary aria-controls={`${id}-content`} id={`${id}-header`}>
            <BadgeCounter
              color="primary"
              children={
                <Typography variant="subtitle1" fontWeight={600}>
                  {title}
                </Typography>
              }
              counter={status?.activeCount || 0}
            />
          </AccordionSummary>
          <AccordionDetails>{content}</AccordionDetails>
        </Accordion>
      ))}

      <Box
        margin={1}
        gap={2}
        textAlign="center"
        display="flex"
        justifyContent="space-evenly"
      >
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={resetAll}
          className={s.button}
        >
          Reset
        </Button>

        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={applyFilters}
          className={s.button}
        >
          Apply
        </Button>
      </Box>
    </div>
  );
}
